import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ClipboardCheck,
  UserCircle,
  BarChart3,
  KeyRound,
  Calendar,
  Activity,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PasswordChangeDialog } from "@/components/PasswordChangeDialog";
import { api } from "@/services/api";

const adminNavItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/users", label: "Users", icon: Users },
  { path: "/statistics", label: "Statistics", icon: BarChart3 },
  { path: "/activity-log", label: "Activity Log", icon: Activity },
  { path: "/settings", label: "Settings", icon: Settings },
];

const reviewerNavItems = [
  { path: "/dashboard", label: "Review Queue", icon: ClipboardCheck },
];

// ── Active Event Banner ────────────────────────────────────────────────────
const ActiveEventBanner = () => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.settings.getActiveEvent()
      .then(data => {
        if (data && (data.event_name || data.event_id)) setEvent(data);
      })
      .catch(() => {});
  }, []);

  if (!event) return null;

  const label = event.event_name
    ? `${event.event_name}${event.event_year ? ` ${event.event_year}` : ""}`
    : event.event_id;

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-indigo-950 text-white text-xs">
      <Calendar className="h-3.5 w-3.5 text-indigo-300 shrink-0" strokeWidth={1.5} />
      <span className="text-indigo-300">Active Event:</span>
      <span className="font-medium truncate">{label}</span>
    </div>
  );
};

// ── Main Layout ────────────────────────────────────────────────────────────
export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const navItems = user?.role === "admin" ? adminNavItems : reviewerNavItems;

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } catch {
      // best-effort — still clear local state
    }
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile header */}
      <div className="lg:hidden glass fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          EventsAir Approvals
        </span>
        <div className="w-10" />
      </div>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/20 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transition-transform duration-300 flex flex-col",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-200 shrink-0">
          <h1
            className="text-xl font-semibold text-indigo-950"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            EventsAir
          </h1>
          <p className="text-sm text-slate-500 mt-1">Approval Hub</p>
        </div>

        {/* Active Event Banner — visible to all users */}
        <ActiveEventBanner />

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-indigo-950 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                )}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200 shrink-0">
          <div className="flex items-center gap-3 px-4 py-3 rounded-md bg-slate-50">
            <UserCircle className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.full_name}
              </p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full mt-2 text-slate-600 hover:text-indigo-700 hover:bg-indigo-50"
            onClick={() => setShowChangePassword(true)}
          >
            <KeyRound className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Change Password
          </Button>
          <Button
            variant="ghost"
            className="w-full mt-1 text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">{children}</div>
      </main>

      {/* Change Password Dialog */}
      <PasswordChangeDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onPasswordChanged={() => setShowChangePassword(false)}
        isFirstLogin={false}
      />
    </div>
  );
};
